/**
 * « Cahiers », côté élève : le catalogue, le sommaire d'un cahier, puis un
 * chapitre à cocher (Hedi, 5 octobre 2026).
 *
 * Le même va-et-vient que chez le parent (`v2/CatalogueScreen`,
 * `ChapitresScreen`, `ChapitreScreen`), avec ce qui se partage pris dans `ui/`
 * — la ligne à cocher (`ui/cahiers`), la copie gardée le temps de la session
 * (`ui/enCache`) — et ce qui diffère écrit ici :
 *
 * - **aucun identifiant d'enfant dans l'adresse** (invariant 2) : les appels
 *   passent par `/me/ouvrages`, et le serveur lit l'élève dans la session ;
 * - **« pas encore vu en classe » se lit et ne se déclare pas.** Chez le parent
 *   la mention est un bouton qui atteste que le cours a eu lieu ; c'est un
 *   adulte qui l'atteste, et l'élève qui compose sa propre feuille ne peut pas
 *   ouvrir par là ce qu'il a envie de travailler. Il peut cocher l'exercice
 *   quand même : rien n'est barré, et la mention dit ce qu'il choisit ;
 * - **ajouter mène à la feuille en cours**, que l'élève clôt quand il veut.
 *
 * Le corrigé ne traverse pas ces routes (règle 2) : il ne s'ouvre qu'à la
 * clôture, sur « Terminés ».
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import { MON_ENTRAINEMENT } from "@/child/monEntrainement";
import { ExerciceACocher, niveauEnClair } from "@/ui/cahiers";
import { oublierMesOuvrages, useContenuDOuvrage } from "@/ui/enCache";
import { etiquette } from "@/ui/matieres";
import { Button, Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";

const CAHIERS = `${MON_ENTRAINEMENT}/cahiers`;

/** Les deux causes d'un catalogue vide, dans les mots de l'élève. Elles
 *  appellent deux gestes différents, et ni l'un ni l'autre n'est le sien. */
const MANQUE: Record<string, string> = {
  aucune_matiere_principale:
    "Tes matières principales ne sont pas encore fixées : demande à tes parents de les régler.",
  aucun_ouvrage: "Tes matières principales n'ont pas encore de cahier dans l'application.",
};

export function MesCahiersScreen() {
  const { donnees: catalogue, erreur } = useContenuDOuvrage("mesOuvrages", {});
  const ouvrages = catalogue?.ouvrages ?? [];

  return (
    <Screen title="Mes cahiers">
      {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}
      {!catalogue && !erreur ? <Loading /> : null}

      {catalogue && ouvrages.length === 0 ? (
        <Card>
          <Empty>{MANQUE[catalogue.manque ?? ""] ?? "Aucun cahier pour toi."}</Empty>
        </Card>
      ) : null}

      {ouvrages.length > 0 ? (
        <Card>
          <ul className="list">
            {ouvrages.map((ouvrage) => (
              <li key={ouvrage.cle} className="list__item">
                <div>
                  <Link to={`${CAHIERS}/${encodeURIComponent(ouvrage.cle)}`}>
                    <strong>{ouvrage.titre}</strong>
                  </Link>
                  <p className="muted small">
                    {etiquette(ouvrage.matiere, ouvrage.matiere_libelle)} — {ouvrage.chapitres}{" "}
                    chapitres
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </Screen>
  );
}

export function MonCahierScreen() {
  const { ouvrage = "" } = useParams();
  const { donnees: sommaire, erreur } = useContenuDOuvrage("mesChapitres", {
    params: { ouvrage },
  });
  const chapitres = sommaire?.chapitres ?? [];

  return (
    <Screen title={sommaire?.ouvrage_titre ?? "Le cahier"}>
      <p className="notice">
        <Link to={CAHIERS}>← Tous mes cahiers</Link>
      </p>

      {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}
      {!sommaire && !erreur ? <Loading /> : null}

      {/* Dite une fois, en tête, comme chez le parent : un sommaire qui
          raccourcit sans un mot se lit comme un découpage raté. */}
      {sommaire?.niveau ? (
        <p className="muted small">
          Ce cahier couvre plusieurs années : seuls les chapitres de{" "}
          {niveauEnClair(sommaire.niveau)} sont affichés.
        </p>
      ) : null}

      {sommaire && chapitres.length === 0 ? (
        <Card>
          <Empty>Ce cahier n'a pas encore d'exercice pour toi.</Empty>
        </Card>
      ) : null}

      {chapitres.length > 0 ? (
        <Card>
          <ul className="list">
            {chapitres.map((chapitre) => (
              <li key={chapitre.cle} className="list__item">
                <div>
                  <Link to={`${CAHIERS}/${encodeURIComponent(ouvrage)}/${encodeURIComponent(chapitre.cle)}`}>
                    {chapitre.titre}
                  </Link>
                  <p className="muted small">
                    {chapitre.page ? `page ${chapitre.page} — ` : ""}
                    {chapitre.exercices} exercices
                    {chapitre.faits > 0 ? `, dont ${chapitre.faits} déjà faits` : ""}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </Screen>
  );
}

export function MonChapitreScreen() {
  const { ouvrage = "", cle = "" } = useParams();
  const navigate = useNavigate();
  const { donnees: chapitre, erreur: erreurDeCharge } = useContenuDOuvrage("monChapitre", {
    params: { ouvrage, cle },
  });
  const [erreurDEnvoi, setErreurDEnvoi] = useState<string | null>(null);
  const erreur = erreurDEnvoi ?? erreurDeCharge;
  const [cochees, setCochees] = useState<string[]>([]);
  const [envoi, setEnvoi] = useState(false);
  const choisies = useMemo(() => new Set(cochees), [cochees]);

  // La sélection appartient au chapitre qu'on quitte : la reporter sur le
  // suivant ferait ajouter autre chose que ce qui est coché à l'écran.
  useEffect(() => {
    setCochees([]);
    setErreurDEnvoi(null);
  }, [ouvrage, cle]);

  const lignes = chapitre?.lignes ?? [];

  const basculer = (exerciseId: string) =>
    setCochees((avant) =>
      avant.includes(exerciseId)
        ? avant.filter((autre) => autre !== exerciseId)
        : [...avant, exerciseId],
    );

  const ajouter = async () => {
    setEnvoi(true);
    setErreurDEnvoi(null);
    try {
      await call("composerMonEntrainement", {
        params: { ouvrage, cle },
        body: { exercise_ids: cochees },
      });
      // Le compte des exercices déjà faits change sur le sommaire et le
      // catalogue : garder la copie dirait le contraire de ce qui vient d'être
      // fait.
      oublierMesOuvrages();
      navigate(MON_ENTRAINEMENT);
    } catch (echec) {
      setErreurDEnvoi(echec instanceof ApiError ? echec.message : "L'ajout n'est pas passé.");
      setEnvoi(false);
    }
  };

  return (
    <Screen
      title={chapitre?.titre ?? "Le chapitre"}
      lead={
        chapitre
          ? `${chapitre.ouvrage_titre} — ${etiquette(chapitre.matiere, chapitre.matiere_libelle)}` +
            (chapitre.page ? ` — page ${chapitre.page}` : "")
          : undefined
      }
    >
      <p className="notice">
        <Link to={`${CAHIERS}/${encodeURIComponent(ouvrage)}`}>← Le sommaire</Link>
      </p>

      {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}
      {!chapitre && !erreur ? <Loading /> : null}

      {chapitre && lignes.length === 0 ? (
        <Card>
          <Empty>Ce chapitre n'a pas encore d'exercice pour toi.</Empty>
        </Card>
      ) : null}

      {lignes.length > 0 ? (
        <Card>
          <ul className="list">
            {lignes.map((ligne) => (
              <ExerciceACocher
                key={ligne.exercise_id}
                ligne={ligne}
                cochee={choisies.has(ligne.exercise_id)}
                basculer={() => basculer(ligne.exercise_id)}
                pasEncoreVu="pas encore vu en classe"
              />
            ))}
          </ul>
          <div className="screen__actions">
            <Button type="button" disabled={cochees.length === 0 || envoi} onClick={ajouter}>
              {envoi
                ? "Ajout…"
                : `Ajouter ${cochees.length} exercice${cochees.length > 1 ? "s" : ""} à mon entraînement`}
            </Button>
            <p className="muted small">
              Ils s'ajoutent à ta feuille en cours, que tes parents voient aussi. Tu peux y mettre
              d'autres chapitres avant de la clore.
            </p>
          </div>
        </Card>
      ) : null}
    </Screen>
  );
}
